<script setup lang="ts">
import { countries } from "~/helpers/countries";
import { getMimeType } from "~/helpers/forms";
import { ref, reactive, onMounted } from "vue";
import "intl-tel-input/build/css/intlTelInput.css";
import intlTelInput from "intl-tel-input";
import { navigateTo } from "nuxt/app";
import LinkedinButton from "./jobs/linkedinButton.vue";

const props = defineProps<{ title?: string; location?: string }>();

const uploadFormData = new FormData();

const strapiEnvs = computed(() => ({
  STRAPI_URL: import.meta.env.VITE_BASE_STRAPI_URL,
  STRAPI_TOKEN: import.meta.env.VITE_BASE_STRAPI_TOKEN,
}));

const defaultFormValues = {
  fullname: "",
  email: "",
  country: "",
  telephone: "",
  resumelink: "",
  files: null,
  visa: "",
  noticePeriod: "",
  salaryExpectation: "",
  aboutYourself: "",
  flexCheckDefault: false,
  fileInfo: {},
};

const iti = ref({});
const countryName = ref("");
const form = reactive({ ...defaultFormValues });
const isLoading = ref(false);

// Function to reset the form to its default values
function resetForm() {
  Object.assign(form, { ...defaultFormValues });
}

onMounted(() => {
  const input = document.querySelector("#telephone");
  iti.value = intlTelInput(<HTMLInputElement>input, {
    allowDropdown: true,
    dropdownContainer: null,
    initialCountry: "auto",
    autoPlaceholder: "polite",
    geoIpLookup: (callback) => {
      fetch("https://ipapi.co/json")
        .then((res) => res.json())
        .then((data) => callback(data.country_code))
        .catch(() => callback("us"));
    },
    separateDialCode: true,
    formatOnDisplay: true,
    showFlags: true,
  });
});

async function getCountry() {
  try {
    const response = await fetch("https://ipapi.co/json/");
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data.country_name;
  } catch (error) {
    console.error("There was a problem with the fetch operation:", error);
  }
}

onMounted(() => {
  getCountry().then((name) => {
    countryName.value = name;
  });
});

const formData = new FormData();

const handleFileUpload = (event) => {
  const label = document.getElementById("fileLabel");
  const chooseMessage = document.querySelector(".chosen-file");
  const messageDiv = document.getElementById("message");

  const maxSize = 5 * 1024 * 1024; // 5MB
  const file = event.target.files[0];

  messageDiv.textContent = "";

  if (label) {
    label.textContent = event.target.files[0].name;
    if (label.textContent.length > 10) {
      const nameParts = label.textContent.split(".");
      const extension = nameParts.pop();
      const shortName =
        label.textContent.substring(0, 3) + "..." + label.textContent.slice(-3);
      label.textContent = shortName + "." + extension;
    }

    if (label.textContent !== "No file chosen") {
      chooseMessage.textContent = "File chosen";
    } else {
      chooseMessage.textContent = "No file chosen";
    }


    uploadFormData.set("files", event.target.files[0]);   
    uploadFormData.set(
      "fileInfo",
      JSON.stringify({ name: event.target.files[0].name, folder: null }),
    );
  }
  if (file.type !== getMimeType(label.textContent)) {
    messageDiv.textContent =
      "Error: Invalid file type. Please upload PDF file.";
    return;
  }

  if (file.size > maxSize) {
    messageDiv.textContent = "Error: File size exceeds 5MB.";
    return;
  }
};

async function submitForm() {
  console.log('Submit form')
  console.log('Form',uploadFormData.get('files'))
  isLoading.value = true; // Start loading
  formData.append("fullName", form.fullname);
  formData.append("email", form.email);
  formData.append("country", countryName.value);
  formData.append("telephone", form.telephone);
  formData.append("resumelink", form.resumelink);
  formData.append("visa", form.visa);
  formData.append("noticePeriod", form.noticePeriod);
  formData.append("salaryExpectation", form.salaryExpectation);
  formData.append("aboutYourself", form.aboutYourself);
  formData.append("flexCheckDefault", form.flexCheckDefault);

  const obj = {
    data: {
      fullname: form.fullname,
      email: form.email,
      country: countryName.value,
      telephone: form.telephone,
      resumelink: form.resumelink,
      files: uploadFormData.get("files"),
      visa: form.visa,
      noticePeriod: form.noticePeriod,
      salaryExpectation: form.salaryExpectation,
      aboutYourself: form.aboutYourself,
      flexCheckDefault: form.flexCheckDefault,
      job_link: `${props.title}, ${props.location}`,
    },
  };

  try {
    const upload = await fetch(`${strapiEnvs.value.STRAPI_URL}/api/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${strapiEnvs.value.STRAPI_TOKEN}`,

      },
      body: uploadFormData,
    });

    const response = await fetch("/api/strapi/job-requests", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data: { ...obj.data, resumefile: await upload.json() },
      }),
    });

    // Reset the form after successful submission
    resetForm();

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
  } catch (error) {
    console.error("There was a problem with the fetch operation:", error);
  } finally {
    isLoading.value = false; // End loading
  }
}

const goToLink= ()=>{

  navigateTo('', { external: true })
}

const { data: jobPostData } = useCustomFetch("/api/strapi/job-post-form");


</script>

<style>
.iti {
  --iti-path-flags-1x: url("../node_modules/intl-tel-input/build/img/flags.png");
  --iti-path-flags-2x: url("../node_modules/intl-tel-input/build/img/flags@2x.png");
  --iti-path-globe-1x: url("../node_modules/intl-tel-input/build/img/globe.png");
  --iti-path-globe-2x: url("../node_modules/intl-tel-input/build/img/globe@2x.png");
}
.iti--separate-dial-code,
.iti__selected-country,
.iti__flag-container,
.iti__country-container {
  background-color: rgba(0, 0, 0, 0.05);
}

.iti--allow-dropdown
  .iti__country-container:not(:has(+ input[disabled])):not(
    :has(+ input[readonly])
  ),
.iti__selected-country-primary:hover,
.iti--allow-dropdown
  .iti__country-container:not(:has(+ input[disabled])):not(
    :has(+ input[readonly])
  ),
.iti__selected-country:has(+ .iti__dropdown-content:hover)
  .iti__selected-country-primary {
  background-color: transparent !important;
}
.iti__selected-dial-code {
  margin-left: 4px;
  padding-right: 6px;
}
</style>

<template>
  <div v-if="jobPostData" class="job-post-right">
    <img src="/img/candlecandle.webp" alt="" class="job-candle" />
    <div class="container">
      <h3>{{ jobPostData.title }}</h3>
      <form @submit.prevent="submitForm">
        <div class="form-group">
          <input
            type="text"
            class="form-control"
            id="fullName"
            :placeholder="jobPostData.full_name+'*'"
            v-model="form.fullname"
            name="fullname"
            required
          />
        </div>
        <div class="form-group">
          <input
            type="email"
            class="form-control"
            id="email"
            :placeholder="jobPostData.email+'*'"
            v-model="form.email"
            name="email"
            required
          />
        </div>
        <div class="form-group">
          <select
            name="country"
            id="country"
            class="form-control"
            :placeholder="jobPostData.country+'*'"
            v-model="countryName"
          >
            <option
              v-for="country in countries"
              :value="country.name"
              :key="country.id"
            >
              {{ country.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <input
            type="tel"
            class="form-control"
            id="telephone"
            :placeholder="jobPostData.phone+'*'"
            v-model="form.telephone"
            name="telephone"
            maxlength="15"
            required
          />
        </div>
        <div class="form-group resume">
          <label for="coverLetter">{{ jobPostData.resume }}</label>
          <input
            type="tel"
            class="form-control"
            id="phone"
            placeholder="URL (Linkein, Github , Portofolio)"
            v-model="form.resumelink"
            name="resumelink"
          />
        </div>
        <div class="form-group resumeUpload">
          <label for="coverLetter">{{ jobPostData.resume_file }}</label
          ><br />
          <div class="custom-file-input">
            <input
              type="file"
              class="form-control-file"
              id="resume"
              accept="application/pdf"
              name="resumefile"
              @change="handleFileUpload"
            />
            <label id="fileLabel" for="resume" class="form-button-choose">{{
              jobPostData.choose_file
            }}</label>
            <p class="chosen-file">No file chosen</p>
          </div>
          <div>
            <div id="message" class="message"></div>
            <p class="message-p">
              {{ jobPostData.file_types }}
            </p>
          </div>
        </div>
        <div class="visa-sponsorship">
          <p>{{ jobPostData.visa }} *</p>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              value=""
              id="flexCheckDefault"
              name="flex-checked"
            
              @change="form.flexCheckDefault = true"            
            />
            <label class="form-check-label" for="flexCheckDefault">Yes</label>
  
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              value=""
              id="flexCheckChecked"
              name="flex-checked"
             @change="form.flexCheckDefault = false"
            />
            <label class="form-check-label" for="flexCheckChecked">No</label>
          </div>
          <div class="form-group">
            <input
              type="tel"
              class="form-control"
              id="phone"
              :placeholder="jobPostData.other"
              v-model="form.visa"
              name="visa"
              v-if="form.flexCheckDefault === false ? 'required' : ''"
            />
          </div>
        </div>
        <div class="form-group notice-period">
          <input
            type="date"
            class="form-control"
            id="phone"
            :placeholder="jobPostData.notice_period +'*'"
            v-model="form.noticePeriod"
            name="noticePeriod"
            required
          />
        </div>
        <div class="form-group salary-expectation">
          <input
            type="text"
            class="form-control"
            id="phone"
            :placeholder="jobPostData.salary +'*'"
            v-model="form.salaryExpectation"
            name="salaryExpectation"
            required
          />
        </div>
        <div class="form-group about-yourself">
          <p for="coverLetter">{{ jobPostData.more }}</p>
          <textarea
            class="form-control"
            id="coverLetter"
            rows="5"
            placeholder=""
            v-model="form.aboutYourself"
            name="aboutYourself"
            required
          ></textarea>
        </div>

        <button
          type="submit"
          :disabled="isLoading"
          class="btn btn-primary btn-submit-application"
        >
        <span v-if="isLoading">Submitting...</span>
          <span v-if="isLoading">Submitting...</span>
          <span v-else> {{ jobPostData.button_text }}</span>
        </button>       
      </form>     
    </div>
  </div>
</template>
